import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { Box, Typography, Switch } from "@mui/material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import {
  parentBoxStyle,
  infoStyle,
  yearMonth,
  childBoxStyleSpaceBetween,
} from "services/constants";
import { useSelector, useDispatch } from "react-redux";
import { showToast } from "services/utils/Status";
import {
  specialCharacterRestrict,
  onlyNumberRegex,
  specialCharacterRest,
} from "services/constants/patterns";
import {
  minimum3Characters,
  minimum10Digits,
} from "services/constants/ErrorMessages";
import moment from "moment";
import { CustomAutoComplete } from "components/CustomComponents/CustomAutoComplete";
import { CustomMultiSelectionWithLabel } from "components/CustomComponents/CustomMultiSelectionWithLabel";
import { useFetch } from "services/hooks/useFetch";
import {
  userGroupDetailsUrl,
  userGroupOptions,
  selectedUserGroupLabels,
  userGroupIdsFromLabels,
  userGroupRequired,
} from "services/utils/userGroupOptions";
import sessionHandling from "services/utils/notificationUtils";
import { setScreenUserId } from "services/Redux/HRPortal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { tooltipStyleProps } from "services/constants/Styles";
import { SiteSelect } from "pages/Administration/Sites/SiteSelect";

export const EmployeeBasicInfo = ({
  handleNext,
  onUpdate,
  data,
  reCallApi,
  onClose,
  setGetCompanyId,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const {
    register,
    reset,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    unregister,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...data,
      date_joined: moment(new Date()).format("YYYY-MM-DD"),
      onboard_date: moment(new Date()).format("YYYY-MM-DD"),
    },
  });
  const dispatch = useDispatch();
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const [checkBoxValue, setCheckBoxValue] = useState(false);
  const [userNameDetails, setUserNameDetails] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [shiftGradeName, setShiftGradeName] = useState("");
  const [shiftCatgName, setShiftCatgName] = useState("");
  const [shiftGroupName, setShiftGroupName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [employeeGroupName, setEmployeeGroupName] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [dateOfJoining, setDateOfJoining] = useState(
    moment(new Date()).format(yearMonth),
  );
  const [onboardDate, setOnboardDate] = useState(
    moment(new Date()).format(yearMonth),
  );
  const [updateDate, setUpdateDate] = useState(true);
  const [employeeId, setEmployeeId] = useState("");
  const [leaveGroupName, setLeaveGroupName] = useState("");
  const [employmentTypeName, setEmploymentTypeName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [saveAndExit, setSaveAndExit] = useState(false);
  const { result: lastInsertedEmployeeId } = useFetch(
    `${domain}employee-id/last-used`,
  );
  const { result: department } = useFetch(`${domain}department/`);
  const { result: designation } = useFetch(`${domain}designation/`);
  const { result: employmentType } = useFetch(`${domain}employment-type/`);
  const { result: userGroup } = useFetch(`${domain}leave-groups-basics/`);
  const { result: companyList } = useFetch(`${domain}get-active-company/`);
  const { result: shiftGradeData } = useFetch(
    `${domain}administration/shiftroster/shiftgrade/shift-grades/`,
  );
  const { result: shiftGroupData } = useFetch(
    `${domain}administration/shiftroster/shiftgroups/shift-groups/`,
  );
  const { result: shiftCatgList } = useFetch(
    `${domain}administration/shiftroster/shiftrota/shift-categories?status=Active`,
  );
  // Groups are listed for the company picked on this form (see
  // userGroupDetailsUrl); the topbar company stands in until one is picked.
  const { result: userGroupList } = useFetch(
    userGroupDetailsUrl(domain, watch("company_id"), requestCompanyId),
  );
  const userGroupChoices = userGroupOptions(userGroupList);
  // The multi-select has no input ref, so the field is registered by hand.
  if (checkBoxValue) {
    register("user_group", { validate: userGroupRequired });
  }
  const { result: employeeGroup } = useFetch(`${domain}employee-group/?company_id=${requestCompanyId}`);

  let multipleCallRestriction = true;

  useEffect(() => {
    if (updateDate) {
      setValue("date_joined", moment(new Date()).format(yearMonth));
      setValue("onboard_date", moment(new Date()).format(yearMonth));
      setUpdateDate(false);
    }
  }, [setValue, updateDate]);

  const updateUserName = (event) => {
    if (
      specialCharacterRestrict.test(event.target.value) &&
      event.target.value.length <= 32
    ) {
      setUserNameDetails(event.target.value);
      setValue("user_name", event.target.value);
    }
  };

  const handleEmployeeId = (event) => {
    if (
      specialCharacterRest.test(event.target.value) &&
      event.target.value.length <= 12
    ) {
      setValue("employee_id", event.target.value);
      setEmployeeId(event.target.value);
    }
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setValue("company", data?.label);
      setValue("company_id", data?.id);
      setCompanyName(data?.label);
    } else {
      setValue("company", "");
      setValue("company_id", null);
      setCompanyName("");
    }
    // The User Group list is scoped to the company, so a group picked for
    // the previous company may not exist for this one.
    setValue("usergroup_name", []);
    setValue("user_group", []);
  };

  const handleEmployeeGroup = (event, data) => {
    if (data) {
      setValue("employee_group", data?.label);
      setValue("employee_group_id", data?.group_id);
      setEmployeeGroupName(data?.label);
    } else {
      setValue("employee_group", "");
      setValue("employee_group_id", null);
      setEmployeeGroupName("");
    }
  };

  const handleDepartment = async (event, data) => {
    if (data) {
      setValue("departmentName", data?.label);
      setValue("department_id", data?.id);
      setDepartmentName(data?.label);
    } else {
      setValue("departmentName", "");
      setValue("department_id", null);
      setDepartmentName("");
    }
  };

  const handleShiftGradeName = (event, data) => {
    if (data) {
      setValue("shiftGradeName", data?.label);
      setValue("shift_grade", data?.id);
      setShiftGradeName(data?.label);
      setValue("shiftCatgName", "");
      setValue("shift_category", null);
      setValue("shiftGroup", "");
      setShiftGroupName("");
    } else {
      setValue("shiftGradeName", "");
      setValue("shift_grade", null);
      setShiftGradeName("");
      setValue("shiftCatgName", "");
      setValue("shift_category", null);
    }
  };

  const handleShiftCatgName = (event, data) => {
    if (data) {
      setValue("shiftCatgName", data?.label);
      setValue("shift_category", data?.id);
      setShiftCatgName(data?.label);
    } else {
      setValue("shiftCatgName", "");
      setValue("shift_category", null);
      setShiftCatgName("");
    }
  };

  const handleDesignation = (event, data) => {
    if (data) {
      setValue("designation", data?.label);
      setValue("designation_id", data?.id);
      setDesignationName(data?.label);
    } else {
      setValue("designation", "");
      setValue("designation_id", null);
      setDesignationName("");
    }
  };

  const handleDateOfJoined = (event) => {
    setValue("date_joined", event.target.value);
    setDateOfJoining(event.target.value);
    setValue("onboard_date", event.target.value);
    setOnboardDate(event.target.value);
  };

  const handleOnboardDate = (event) => {
    setValue("onboard_date", event.target.value);
    setOnboardDate(event.target.value);
  };

  const handleEmploymentType = (event, data) => {
    if (data) {
      setValue("employmentType", data?.label);
      setValue("employment_type_id", data?.id);
      setEmploymentTypeName(data?.label);
    } else {
      setValue("employmentType", "");
      setValue("employment_type_id", null);
      setEmploymentTypeName("");
    }
  };

  const handleUserLeaveGroup = (event, data) => {
    if (data) {
      setValue("leaveGroup", data?.label);
      setValue("leave_group_id", data?.id);
      setValue("leaveGroupCount", data?.overall_leave_count);
      setLeaveGroupName(data?.label);
    } else {
      setValue("leaveGroup", "");
      setValue("leave_group_id", null);
      setValue("leaveGroupCount", 0);
      setLeaveGroupName("");
    }
  };

  const updateCheckBoxValue = (event) => {
    setCheckBoxValue(event.target.checked);
    setValue("usergroup_name", []);
    setValue("user_group", []);
    if (!event.target.checked) {
      clearErrors("user_group");
      unregister("user_group");
      setValue("user_group", []);
    }
  };

  const updatePhoneNumber = (event) => {
    if (
      event.target.value.length <= 15 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      setPhoneNumber(event.target.value);
      setValue("phone_number", event.target.value);
    }
  };

  const onSubmit = async (data) => {
    if (multipleCallRestriction) {
      setValue("button_loader", true);
      multipleCallRestriction = false;

      const validateResponse = await customGetAPI(
        `${domain}user_tenant/validate/?email=${encodeURIComponent(
          data?.email || "",
        )}&phone_number=${encodeURIComponent(data?.phone_number || "")}`,
      );
      if (!validateResponse?.ok) {
        multipleCallRestriction = true;
        setValue("button_loader", false);
        return;
      }

      onUpdate(data);
      const payload = {
        ...data,
        user_name: data?.user_name.trim(),
        portal_access: checkBoxValue,
      };
      const response = await customAPI(
        `${domain}employee-onboard`,
        "POST",
        payload,
      );
      if (response.ok) {
        if (saveAndExit) {
          showToast("success", "Employee details created.");
          setValue("button_loader", false);
          multipleCallRestriction = true;
          onClose();
          reCallApi(true);
          reset();
        } else {
          dispatch(setScreenUserId(response.responseDetails.user_id));
          setGetCompanyId(payload?.company_id);
          handleNext(1);
          reset();
        }
      } else {
        multipleCallRestriction = false;
        setValue("button_loader", false);
      }
    }
  };

  const handleShiftGroup = (event, data) => {
    if (data) {
      setValue("shiftGroup", data?.label);
      setValue("shift_group", data?.id);
      setShiftGroupName(data?.label);
    }
  };

  const handleUserGroups = (labels) => {
    setValue("usergroup_name", labels);
    setValue("user_group", userGroupIdsFromLabels(userGroupChoices, labels), {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (data && Object.keys(data)?.length > 0) {
      reset(data);
    }
  }, [data, reset]);

  return (
    <Box sx={parentBoxStyle}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ p: 1, m: 1, border: "none" }}>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={12}>
              <Typography sx={{ fontWeight: 600 }}>Company</Typography>
              <Typography {...infoStyle}>
                Select the company entity the employee belongs to.
              </Typography>
              <CustomAutoComplete
                dataList={companyList.map((obj) => ({
                  ...obj,
                  label: obj.companyname,
                }))}
                selectedValue={companyName}
                rules={{
                  ...register("company", {
                    required: "Company name is required",
                  }),
                }}
                updateDataFunction={handleCompanyName}
                placeHolder="Select the company name"
                errorMessage={
                  errors?.company?.message ? errors?.company?.message : ""
                }
                textBoxError={errors.company ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Box>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Basic Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employee Name</Typography>
              <Typography {...infoStyle}>
                Full legal name of the employee.
              </Typography>
              <CustomTextBox
                selectedValue={userNameDetails}
                rules={{
                  ...register("user_name", {
                    required: "Username is required",
                    maxLength: {
                      value: 32,
                      message: "Maximum 32 characters are required",
                    },
                    validate: {
                      minLengthNoSpaces: (value) => {
                        if (value?.trim().length < 3 && value !== "") {
                          return minimum3Characters;
                        } else {
                          return true;
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updateUserName}
                placeHolder="Enter the name"
                errorMessage={
                  errors?.user_name?.message ? errors?.user_name?.message : ""
                }
                textBoxError={errors.user_name ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>
                Employee Id &nbsp;
                <Mui.Tooltip
                  arrow
                  slotProps={tooltipStyleProps}
                  title={`Last Used : ${lastInsertedEmployeeId.latest_employee_id}`}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      p: 0,
                      m: 0,
                      mt: "-2px",
                      cursor: "pointer",
                      verticalAlign: "middle",
                      color: "text.secondary",
                    }}
                  />
                </Mui.Tooltip>
              </Typography>
              <Typography {...infoStyle}>
                Unique id number assigned to the employee.
              </Typography>

              <CustomTextBox
                selectedValue={employeeId}
                rules={{
                  ...register("employee_id", {
                    required: "Employee id is required",
                    validate: {
                      employeeIdAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}employee-id/check?employee_id=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Employee id already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handleEmployeeId}
                placeHolder="Enter the employee id"
                errorMessage={
                  errors?.employee_id?.message
                    ? errors?.employee_id?.message
                    : ""
                }
                textBoxError={errors.employee_id ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Date of Joining</Typography>
              <Typography {...infoStyle}>
                The official date the employee starts working (an employment
                fact).
              </Typography>
              <CustomTextBox
                type="date"
                selectedValue={dateOfJoining}
                rules={{
                  ...register("date_joined", {
                    required: "Date of joined is required",
                  }),
                }}
                updateDataFunction={handleDateOfJoined}
                errorMessage={
                  errors?.date_joined?.message
                    ? errors?.date_joined?.message
                    : ""
                }
                textBoxError={errors.date_joined ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>
                Platform Onboarding Date
              </Typography>
              <Typography {...infoStyle}>
                The date the employee was onboarded onto OrganFlow (a
                platform fact, not an employment fact).
              </Typography>
              <CustomTextBox
                type="date"
                selectedValue={onboardDate}
                // minDate={dateOfJoining}
                rules={{
                  ...register("onboard_date", {
                    required: "Onboard date is required",
                    // validate: (value) =>
                    //   !dateOfJoining ||
                    //   new Date(value) >= new Date(dateOfJoining) ||
                    //   "Onboarding date cannot be before Date of Joining",
                  }),
                }}
                updateDataFunction={handleOnboardDate}
                errorMessage={
                  errors?.onboard_date?.message
                    ? errors?.onboard_date?.message
                    : ""
                }
                textBoxError={errors.onboard_date ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Phone No</Typography>
              <Typography {...infoStyle}>
                Primary mobile number for communication.
              </Typography>
              <CustomTextBox
                selectedValue={phoneNumber}
                rules={{
                  ...register("phone_number", {
                    required: "Phone number is required",
                    minLength: {
                      value: 10,
                      message: minimum10Digits,
                    },
                    validate: {
                      employeePhonenumberAvailable: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}unique/phone-number/?phone_no=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Phone number already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={updatePhoneNumber}
                placeHolder="Enter the phone number"
                errorMessage={
                  errors?.phone_number?.message
                    ? errors?.phone_number?.message
                    : ""
                }
                textBoxError={errors.phone_number ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Email ID</Typography>
              <Typography {...infoStyle}>
                Official email ID used for communication.
              </Typography>
              <Mui.TextField
                size="small"
                {...register("email", {
                  required: false,
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid e-mail address",
                  },
                  validate: {
                    emailAvailable: async (fieldValue) => {
                      if (fieldValue !== "") {
                        try {
                          const response = await fetch(
                            `${domain}unique/email/?email=${fieldValue}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            },
                          );
                          const res = await response.json();
                          if (response.ok) {
                            if (res.exists) {
                              return "Email already exists";
                            } else {
                              return true;
                            }
                          } else if (response.status === 409) {
                            sessionHandling();
                          } else {
                            throw new Error('REQUEST_FAILED');
                          }
                        } catch (error) {
                          console.error('[Employeebasicinfo] operation failed:', error);
                          showToast("error", "Couldn't save employee details. Please try again.");
                        }
                      }
                    },
                  },
                })}
                error={Boolean(errors.email)}
                helperText={errors.email ? errors.email.message : null}
                placeholder="Enter the email"
                fullWidth
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Employment Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employee Group</Typography>
              <Typography {...infoStyle}>Select the employee group.</Typography>
              <CustomAutoComplete
                dataList={employeeGroup.map((obj) => ({
                  ...obj,
                  label: obj.group_name,
                }))}
                selectedValue={employeeGroupName}
                rules={{
                  ...register("employee_group", {
                    required: "Employee group is required",
                  }),
                }}
                updateDataFunction={handleEmployeeGroup}
                placeHolder="Select the employee group"
                errorMessage={
                  errors?.employee_group?.message
                    ? errors?.employee_group?.message
                    : ""
                }
                textBoxError={errors.employee_group ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Employment Type</Typography>
              <Typography {...infoStyle}>
                Choose the work type like Full-time.
              </Typography>
              <CustomAutoComplete
                dataList={employmentType.map((obj) => ({
                  ...obj,
                  label: obj.employment_type,
                }))}
                selectedValue={employmentTypeName}
                rules={{
                  ...register("employmentType", {
                    required: "Employment type is required",
                  }),
                }}
                updateDataFunction={handleEmploymentType}
                placeHolder="Select the employment type"
                errorMessage={
                  errors?.employmentType?.message
                    ? errors?.employmentType?.message
                    : ""
                }
                textBoxError={errors.employmentType ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Department</Typography>
              <Typography {...infoStyle}>
                Where the employee will work.
              </Typography>
              <CustomAutoComplete
                dataList={department.map((obj) => ({
                  ...obj,
                  label: obj.department,
                }))}
                selectedValue={departmentName}
                rules={{
                  ...register("departmentName", {
                    required: "Department name is required",
                  }),
                }}
                updateDataFunction={handleDepartment}
                placeHolder="Select the department name"
                errorMessage={
                  errors?.departmentName?.message
                    ? errors?.departmentName?.message
                    : ""
                }
                textBoxError={errors?.departmentName ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Designation</Typography>
              <Typography {...infoStyle}>
                Job title within the organization.
              </Typography>
              <CustomAutoComplete
                dataList={designation.map((obj) => ({
                  ...obj,
                  label: obj.designation,
                }))}
                selectedValue={designationName}
                rules={{
                  ...register("designation", {
                    required: "Designation is required",
                  }),
                }}
                updateDataFunction={handleDesignation}
                placeHolder="Select the designation"
                errorMessage={
                  errors?.designation?.message
                    ? errors?.designation?.message
                    : ""
                }
                textBoxError={errors?.designation ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Shift & Leave Information
          </Mui.Typography>
          <Mui.Grid container spacing={2}>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Grade</Typography>
              <Typography {...infoStyle}>
                Shift level defines working time rules.
              </Typography>
              <CustomAutoComplete
                dataList={shiftGradeData.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={shiftGradeName}
                rules={{
                  ...register("shiftGradeName", {
                    required: "Shift grade is required",
                  }),
                }}
                updateDataFunction={handleShiftGradeName}
                placeHolder="Select the shift grade"
                errorMessage={
                  errors?.shiftGradeName?.message
                    ? errors?.shiftGradeName?.message
                    : ""
                }
                textBoxError={errors?.shiftGradeName ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Site</Typography>
              <Typography {...infoStyle}>
                Work site. Decides PT and LWF.
              </Typography>
              <SiteSelect
                domain={domain}
                value={watch("site_id")}
                onChange={(siteId) => setValue("site_id", siteId)}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Group</Typography>
              <Typography {...infoStyle}>
                Group of shifts based on work pattern.
              </Typography>
              <CustomAutoComplete
                dataList={shiftGroupData.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={shiftGroupName}
                rules={{
                  ...register("shiftGroup", {
                    required: "Shift Group is required",
                  }),
                }}
                updateDataFunction={handleShiftGroup}
                placeHolder="Select the shift group"
                errorMessage={
                  errors?.shiftGroup?.message ? errors?.shiftGroup?.message : ""
                }
                textBoxError={errors?.shiftGroup ? true : false}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Shift Category</Typography>
              <Typography {...infoStyle}>
                Categorized shift type based on role.
              </Typography>
              <CustomAutoComplete
                dataList={shiftCatgList.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={shiftCatgName}
                rules={{
                  ...register("shiftCatgName", {
                    required: "Shift Category name is required",
                  }),
                }}
                updateDataFunction={handleShiftCatgName}
                placeHolder="Select the shift category"
                errorMessage={errors?.shiftCatgName?.message || ""}
                textBoxError={!!errors?.shiftCatgName}
              />
            </Mui.Grid>
            <Mui.Grid item xs={6}>
              <Typography sx={{ fontWeight: 600 }}>Leave Group</Typography>
              <Typography {...infoStyle}>
                Leave policy assigned to the employee.
              </Typography>
              <CustomAutoComplete
                dataList={userGroup.map((obj) => ({
                  ...obj,
                  label: obj.name,
                }))}
                selectedValue={leaveGroupName}
                rules={{
                  ...register("leaveGroup", {
                    required: "Leave group is required",
                  }),
                }}
                updateDataFunction={handleUserLeaveGroup}
                placeHolder="Select the leave group"
                errorMessage={
                  errors?.leaveGroup?.message ? errors?.leaveGroup?.message : ""
                }
                textBoxError={errors?.leaveGroup ? true : false}
              />
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Paper>

        <Mui.Paper sx={{ p: 2, m: 2 }}>
          <Mui.Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: "bold" }}
          >
            Access & Permissions
          </Mui.Typography>
          <Box sx={childBoxStyleSpaceBetween}>
            <Switch checked={checkBoxValue} onChange={updateCheckBoxValue} />
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography sx={{ fontWeight: 600 }}>
                Portal Access
              </Typography>
              <Typography {...infoStyle}>
                The employee will be able to submit their leaves, reimbursement
                claims, view, and generate payslips through the employee portal.
              </Typography>
            </Box>
          </Box>
          {checkBoxValue && (
            <Mui.Grid item xs={12} sx={{ p: 2, mt: 2 }}>
              <Typography sx={{ fontWeight: 600 }}>User Group</Typography>
              <Typography {...infoStyle}>
                Select the user group to define what features and permissions
                the employee can access when using the portal.
              </Typography>
              <CustomMultiSelectionWithLabel
                options={userGroupChoices}
                value={selectedUserGroupLabels(
                  userGroupChoices,
                  watch("user_group"),
                )}
                onChange={handleUserGroups}
                placeholder="Select the user groups"
                error={errors?.user_group}
                helperText={errors?.user_group?.message || ""}
              />
            </Mui.Grid>
          )}
        </Mui.Paper>

        <Mui.Grid container sx={{ padding: 2 }}>
          <Mui.Grid xs={3}>
            <CustomButton
              actionFuntion={() => {
                setSaveAndExit(true);
              }}
              actionName="Save & Exit"
              typeName="submit"
            />
          </Mui.Grid>
          <Mui.Grid xs={6}></Mui.Grid>
          <Mui.Grid
            xs={3}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomButton
              actionFuntion={handleSubmit(onSubmit)}
              actionName="Next"
              typeName="submit"
              disableAction={watch("button_loader")}
            />
          </Mui.Grid>
        </Mui.Grid>

        <Mui.Grid
          container
          sx={{
            paddingBottom: { xs: 2, sm: 4, md: 4, lg: 5, xl: 5, xxl: 5 },
          }}
        />
      </form>
    </Box>
  );
};
